import { ethers } from "ethers";
import bondAbi from "../abis/BondContract.json";
import lendingAbi from "../abis/Lending.json";
import treasuryAbi from "../abis/Treasury.json";
import safeVaultAbi from "../abis/SafeVault.json";
import depositNftAbi from "../abis/DepositNFT.json";
import autoCompoundAbi from "../abis/AutoCompound.json";
import turboAbi from "../abis/Turbo.json";
import tvtAbi from "../abis/TVT.json";
import priceBandAbi from "../abis/PriceBandExecutor.json";
import marketingEngineAbi from "../abis/MarketingEngine.json";
import dePegGuardAbi from "../abis/DePegGuard.json";
import governanceAbi from "../abis/Governance.json";
import reinvestmentAbi from "../abis/ConsolidatedYieldReinvestment.json";
import treasuryExecutorAbi from "../abis/TreasuryExecutor.json";
import selfPayingNettingAbi from "../abis/SelfPayingNetting.json";

/**
 * Turns an ethers contract-revert error into a clean, human-readable
 * message.
 *
 * A revert can bubble up from a sub-call (BondContract → token-buy
 * router → Uniswap pool, Lending → Treasury, …), and ethers only
 * decodes against the contract directly called — which is why a
 * perfectly normal protocol guard often surfaces as the opaque
 * "execution reverted (unknown custom error)". To fix that we:
 *   1. use the named error if ethers already decoded one (`err.revert`);
 *   2. otherwise deep-scan the error object for the raw revert bytes and
 *      decode them against EVERY protocol contract's error set;
 *   3. map the resolved error name to a friendly explanation.
 */

// One Interface per protocol contract. Decoding tries them all, so a
// revert from any module (or a sub-call into another) is recognised.
const PROTOCOL_INTERFACES = [
    bondAbi,
    lendingAbi,
    treasuryAbi,
    safeVaultAbi,
    depositNftAbi,
    autoCompoundAbi,
    turboAbi,
    tvtAbi,
    priceBandAbi,
    marketingEngineAbi,
    dePegGuardAbi,
    governanceAbi,
    reinvestmentAbi,
    treasuryExecutorAbi,
    selfPayingNettingAbi
].map((abi) => new ethers.Interface(abi));

// The token-buy router + the Uniswap V2 pool aren't bundled as ABI
// files, so their error signatures are declared here by hand — a
// deposit's 10% token-buy leg can revert from either of them.
PROTOCOL_INTERFACES.push(
    new ethers.Interface([
        "error NotAdmin(address caller)",
        "error NotBondContract(address caller)",
        "error SwapFailed()",
        "error SwapApprovalFailed()",
        "error InsufficientLiquidity()",
        "error InsufficientInput()",
        "error InsufficientOutput(uint256 amountOut, uint256 amountOutMin)",
        "error InvalidPath()",
        "error NoPair()",
        "error KInvariant()",
        "error Forbidden()",
        "error IdenticalTokens()",
        "error PairExists()"
    ])
);

/**
 * Friendly, user-facing copy per protocol error. When an entry exists it
 * is shown ALONE (no raw `ErrorName(args)` prefix) so the modal reads
 * like a plain sentence.
 */
const FRIENDLY_HINTS = {
    PegNotOk:
        "USDC has de-pegged, so new deposits are paused for safety. Your withdrawals and early exits still work normally — deposits resume automatically once the peg recovers.",
    DepositsDisabled:
        "Deposits are currently paused by protocol governance. Withdrawals and exits are unaffected.",
    DepositBelowMinimum: "The minimum deposit is 100 USDC. Please enter a larger amount.",
    DepositPoolAttached:
        "This deposit is attached to a Consolidated Reinvestment Pool. Detach it from the pool first, then try again.",
    OutstandingLoan:
        "Repay or close the outstanding loan on this deposit before settling, exiting or rolling it over.",
    InvalidDepositState: "This deposit isn't in a state that allows this action right now.",
    NotDepositOwner: "Only the current owner of this deposit can perform this action.",
    UnknownDeposit: "That deposit could not be found on-chain.",
    InsufficientReserve: "The Distribution Reserve is too small for this amount right now. Try a smaller amount.",
    SolvencyFloorBreached: "The Distribution Reserve cannot pay this amount without breaching its safety floor.",
    LtvExceedsMax: "Borrow amount exceeds the maximum LTV (75% of your Safe Vault collateral).",
    LtvBelowLiquidation: "This loan's LTV is below the liquidation threshold, so it cannot be liquidated.",
    LoanAlreadyOpen: "This deposit already has an open loan.",
    LoanNotOpen: "This deposit has no open loan.",
    RepayExceedsDebt: "The repayment amount is larger than the outstanding debt.",
    InvalidDepositForLoan: "The deposit must be Active and owned by you to borrow against it.",
    NoBorrowingAgainstTurbo:
        "Turbo deposits can't be borrowed against — the Turbo product already deploys 100% of the Safe Vault as leverage at creation, so no borrow capacity remains. Open a Standard deposit if you need fresh borrow headroom.",
    TokenPoolAttached: "This NFT is locked because its deposit is in a Reinvestment Pool. Detach it first.",
    TransferNotWhitelisted: "NFT transfers must go through an approved marketplace contract.",
    MissingRole: "Your wallet doesn't hold the role required for this action.",
    ReentrantCall: "The transaction was rejected by the contract's re-entrancy guard. Please retry.",
    GracePeriodActive: "The grace period is still active — this action isn't available yet.",
    MaturityNotReached: "This deposit hasn't reached maturity yet.",
    TransferFailed: "A token transfer failed — check your USDC balance and allowance, then retry.",
    CapitalRouterTransferFailed: "The protocol couldn't route the deposit's capital. Please retry.",
    // Token-buy leg (GilderTokenBuyRouter + the GIL/USDC Uniswap pool).
    SwapFailed: "The deposit's GIL token-buy leg failed on the GIL/USDC pool. Please retry.",
    SwapApprovalFailed: "The token-buy router could not approve the swap. Please retry.",
    NoPair: "The GIL/USDC liquidity pool isn't set up yet — the deposit's token-buy leg has nowhere to trade.",
    InsufficientLiquidity:
        "The GIL/USDC pool doesn't have enough liquidity for this deposit's token-buy leg right now.",
    InsufficientOutput:
        "The GIL token-buy leg would exceed the slippage limit on the current pool — try again shortly.",
    KInvariant: "The GIL/USDC pool rejected the swap (constant-product check). Please retry.",
    // §I.6 Consolidated Yield Reinvestment errors.
    UnknownPool: "That pool doesn't exist — refresh and try again.",
    NotPoolOwner: "Only the pool's owner can perform this action.",
    DepositNotActive: "Only Active term deposits can be attached to a pool.",
    AlreadyAttached: "This deposit is already attached to a pool.",
    NotAttached: "This deposit isn't attached to any pool.",
    CyrNotInitialised: "The Consolidated Reinvestment module isn't fully initialised yet — try again shortly.",
    PoolCapacityReached: "This pool has reached its participant cap. Detach an older deposit before attaching a new one.",
    PoolTypeMismatch:
        "This deposit's pool-type preference doesn't match the pool. A Standard pool only accepts Standard-mode deposits; a Turbo pool only accepts Turbo-mode deposits. Update the deposit's preference from Settings, or create a pool of the matching type.",
    SweepThresholdNotMet:
        "The pool's combined yield is still below the 102 USDC sweep threshold (100 USDC compoundable + 2 USDC keeper fee). Wait for more interest to accrue.",
    InsufficientLeverageCapacity:
        "This Turbo pool doesn't have enough borrowing headroom across its members to draw the required leverage. Attach more deposits, repay outstanding loans, or wait for accrued interest to grow the collateral."
};

/**
 * @param {unknown} err Ethers-style revert error.
 * @returns {string} Human-readable message.
 */
// Plain `require(..., "string")` reverts (e.g. MockUSDC / standard ERC20)
// arrive as Error(string) or err.reason — not a named custom error — so they'd
// otherwise surface raw ("MockUSDC: balance"). Map the common ones to a clear,
// actionable sentence.
const STRING_REVERT_HINTS = [
    {
        match: /MockUSDC:\s*balance|transfer amount exceeds balance|insufficient balance|ERC20InsufficientBalance/i,
        msg: "You don't have enough USDC in your wallet to cover this. Top up USDC and try again."
    },
    {
        match: /MockUSDC:\s*allowance|insufficient allowance|ERC20InsufficientAllowance/i,
        msg: "USDC spending approval is too low. Approve USDC for this action, then retry."
    }
];

function mapErrorString(s) {
    const str = String(s || "");
    for (const { match, msg } of STRING_REVERT_HINTS) {
        if (match.test(str)) return msg;
    }
    return null;
}

export function decodeContractError(err) {
    if (!err) return "Something went wrong. Please try again.";

    // 1. ethers v6 may have already decoded the custom error for us.
    const preDecoded = findRevertName(err);
    if (preDecoded) {
        return formatKnown(preDecoded.name, preDecoded.args);
    }

    // 2. Deep-scan for raw revert bytes and decode against every ABI.
    for (const data of collectHexData(err)) {
        for (const iface of PROTOCOL_INTERFACES) {
            try {
                const parsed = iface.parseError(data);
                if (parsed) {
                    return formatKnown(parsed.name, parsed.args);
                }
            } catch {
                // Not an error from this ABI — try the next one.
            }
        }
    }

    // 3. Plain user rejection — keep it short and unalarming.
    const code = err.code || err.cause?.code;
    if (code === "ACTION_REJECTED" || /user rejected|user denied/i.test(String(err?.message || ""))) {
        return "You rejected the transaction in your wallet.";
    }

    // 4. Fallback to ethers' own (shortest) message — but first map common
    //    string reverts (insufficient balance/allowance) to a clear sentence.
    const fallback =
        err.shortMessage ||
        err.reason ||
        err.cause?.shortMessage ||
        err.message ||
        "Transaction failed.";
    return mapErrorString(fallback) || fallback;
}

/** Friendly sentence for a known error, else a compact `Name(args)`. */
function formatKnown(name, args) {
    // Standard Solidity reverts.
    if (name === "Error") {
        const raw = String(args?.[0] || "");
        return mapErrorString(raw) || raw || "The transaction was reverted by the contract.";
    }
    if (name === "Panic") {
        return "The contract hit an internal panic (arithmetic/overflow). Please retry or report this.";
    }
    const hint = FRIENDLY_HINTS[name];
    if (hint) return hint;
    const rendered = (args || []).map((a) => (typeof a === "bigint" ? a.toString() : String(a)));
    return rendered.length ? `${name}(${rendered.join(", ")})` : name;
}

/**
 * Returns `{ name, args }` if ethers already decoded the custom error
 * anywhere in the error chain (`err.revert`), else null.
 */
function findRevertName(err, depth = 0) {
    if (!err || typeof err !== "object" || depth > 6) return null;
    if (err.revert && err.revert.name) {
        return { name: err.revert.name, args: err.revert.args || [] };
    }
    if (err.cause) return findRevertName(err.cause, depth + 1);
    return null;
}

/**
 * Recursively walks the error object collecting every string that looks
 * like ABI-encoded revert data (`0x` + an even number of hex chars, at
 * least a 4-byte selector). Different wallets / RPCs bury the data at
 * different paths, so a deep scan is the only reliable way to find it.
 */
function collectHexData(err) {
    const found = new Set();
    const seen = new Set();

    const looksLikeRevertData = (s) =>
        typeof s === "string" &&
        /^0x[0-9a-fA-F]+$/.test(s) &&
        s.length >= 10 &&
        s.length % 2 === 0;

    const walk = (node, depth) => {
        if (node == null || depth > 7) return;
        if (typeof node === "string") {
            if (looksLikeRevertData(node)) found.add(node);
            // Some RPCs embed the hex inside a longer message string.
            else if (typeof node === "string") {
                const m = node.match(/0x[0-9a-fA-F]{8,}/g);
                if (m) m.forEach((h) => found.add(h));
            }
            return;
        }
        if (typeof node !== "object" || seen.has(node)) return;
        seen.add(node);
        for (const key of Object.keys(node)) {
            try {
                walk(node[key], depth + 1);
            } catch {
                /* a throwing getter — ignore */
            }
        }
    };

    walk(err, 0);
    // Longest first — an error WITH args carries more signal than a bare
    // 4-byte selector that might collide.
    return [...found].sort((a, b) => b.length - a.length);
}